/**
 * CalendarProviderChips — the practitioners above the calendar as the Practice calendar draws them: one compact
 * chip per practitioner (photo with its availability dot, name, specialty, and a count badge: today's free slots,
 * red when the practitioner has an issue that asks for action), "+N" for the chips that do not fit.
 *
 * Nothing is lost against the Signal DS strip of full cards: a click on a chip opens that practitioner's card in a
 * popover (`ProviderBadge`, Practice's clinic access badge): hours, the slots popover, the next free slots (each one starts a
 * booking), the quick-book chips and the ✓ that puts the practitioner in or out of the filter. A practitioner who is
 * out of a narrowed selection shows a blue ring. On by default.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  AnchoredPopover,
  AnchoredPopoverContent,
  AnchoredPopoverTrigger,
  AvailabilityDot,
  Avatar,
  type PACalendarProviderStripProps,
} from '@scripsteam/scrips-signal-ds';
import type { CreateAppointmentProfileRequest } from '@/types/scheduling-api';
import { ProviderBadge } from './provider-badge';
import type { CalendarProviderSummary } from '@/scheduling/lib/calendar-mapping';

type Props = Omit<
  PACalendarProviderStripProps<CreateAppointmentProfileRequest>,
  'isExpanded' | 'onCanExpandChange' | 'providers'
> & {
  providers: CalendarProviderSummary[];
  /** The practitioners' slot feed is loading: while no practitioner is known yet, grey chips stand in (Practice
   *  `provider-strip.tsx:316-319`, `loading && providers.length === 0`). */
  loading?: boolean;
  /** The cards' Retry when the slot feed failed (`slotsState: 'error'`): asks the feed again (PM `onRetrySlots`). */
  onRetrySlots?: () => void;
};
type Provider = Props['providers'][number];

/** Practice's hover timings (`provider-strip.tsx`): open after 300 ms, close 250 ms after the pointer leaves. */
const SHOW_DELAY = 300;
const HIDE_DELAY = 250;

/** The room a "+N" chip keeps back (its width and a gap), in px: layout arithmetic, not a style. */
const CHIP_GAP = 8;
const MORE_WIDTH = 48;
/** Five loading chips; their widths (Practice `provider-strip.tsx:368`) live in `showcase-calendar.css` by position. */
const SKELETON_CHIPS = [0, 1, 2, 3, 4] as const;

/** The first letter of the name, past a title ("Dr. Nader" → N): PM `provider-strip.tsx` `initialOf`. */
const initialOf = (name: string) => (name.replace(/^(dr|prof|mr|mrs|ms)\.?\s+/i, '').trim()[0] ?? '?').toUpperCase();

function Chip({ p, ring }: { p: Provider; ring: boolean }) {
  const needsAction = !!p.issue?.message;
  return (
    <span className="cal-chip" data-ring={ring ? '' : undefined}>
      <span className="cal-chip-photo">
        {/* PM: the photo, or the initial on a grey-200 rounded square (not the DS round disc). */}
        {p.photo ? (
          <Avatar scale="compact" tone="provider" name={p.name} src={p.photo} size={32} />
        ) : (
          <span aria-hidden data-avatar-fallback className="cal-chip-initial">
            {initialOf(p.name)}
          </span>
        )}
        {/* No availability without the slot feed (PM: no dot while `slotsState` is set). */}
        {p.slotsState ? null : <AvailabilityDot available={p.available} className="cal-chip-dot" />}
      </span>
      <span className="cal-chip-text">
        <span className="cal-chip-name">{p.name}</span>
        <span className="cal-chip-specialty">{p.specialty}</span>
      </span>
      <span
        className="cal-chip-count"
        data-action={needsAction ? '' : undefined}
        title={p.slotsState === 'error' ? "Slots couldn't load" : p.issue?.message ?? undefined}
      >
        {p.slotsState ? '—' : p.slotsToday}
      </span>
    </span>
  );
}

export function CalendarProviderChips({
  providers,
  selectedProviderIds,
  onToggleProvider,
  onProviderClick,
  onSelectProfile,
  onSelectSlot,
  organizationId,
  renderSlotsPopover,
  loading = false,
  onRetrySlots,
}: Props) {
  const selected = selectedProviderIds ? new Set(selectedProviderIds) : null;
  const narrowed = !!selected && selected.size > 0 && providers.some((p) => !selected.has(p.id));
  const rowRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState(providers.length);
  const [expanded, setExpanded] = useState(false);

  // Hover card, as the Practice strip does it: the pointer over a chip opens its card after 300 ms (at once when
  // another card is already open), the card stays while the pointer is on the chip or on it and closes 250 ms after
  // it leaves; a click pins it open until Escape or a click outside; focus opens it at once, Enter / Space pin it.
  const [open, setOpen] = useState<{ id: string; pinned: boolean } | null>(null);
  const showTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);
  const clearShow = () => {
    if (showTimer.current !== null) window.clearTimeout(showTimer.current);
    showTimer.current = null;
  };
  const cancelClose = () => {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };
  const show = (id: string) => {
    cancelClose();
    clearShow();
    setOpen((o) => {
      if (o) return o.pinned && o.id !== id ? o : { id, pinned: o.id === id ? o.pinned : false };
      showTimer.current = window.setTimeout(() => {
        setOpen({ id, pinned: false });
        showTimer.current = null;
      }, SHOW_DELAY);
      return null;
    });
  };
  const hideSoon = () => {
    cancelClose();
    clearShow();
    closeTimer.current = window.setTimeout(() => setOpen((o) => (o?.pinned ? o : null)), HIDE_DELAY);
  };
  useEffect(
    () => () => {
      clearShow();
      cancelClose();
    },
    [],
  );

  useLayoutEffect(() => {
    const row = rowRef.current;
    const measure = measureRef.current;
    if (!row || !measure) return;
    const recompute = () => {
      const widths = Array.from(measure.children).map((c) => (c as HTMLElement).offsetWidth);
      let used = 0;
      let n = 0;
      for (const w of widths) {
        const next = used + (n > 0 ? CHIP_GAP : 0) + w;
        const reserve = n + 1 < widths.length ? CHIP_GAP + MORE_WIDTH : 0;
        if (next + reserve > row.clientWidth) break;
        used = next;
        n++;
      }
      setFit(Math.max(1, n));
    };
    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(row);
    return () => ro.disconnect();
  }, [providers]);

  const shown = expanded ? providers : providers.slice(0, fit);
  const rest = expanded ? 0 : providers.length - shown.length;

  return (
    <div data-slot="pa-calendar-provider-chips" className="cal-chips">
      <div ref={measureRef} aria-hidden inert className="cal-chips-measure">
        {providers.map((p) => (
          <Chip key={p.id} p={p} ring={false} />
        ))}
      </div>
      <div ref={rowRef} role="list" aria-label="Practitioners" className="cal-chips-row" data-expanded={expanded ? '' : undefined}>
        {loading && providers.length === 0
          ? SKELETON_CHIPS.map((i) => (
              <span key={i} aria-hidden data-skeleton-chip className="cal-chip-skeleton">
                <span className="cal-chip-skeleton-bar cal-chip-skeleton-photo animate-pulse motion-reduce:animate-none" />
                <span className="cal-chip-skeleton-bar animate-pulse motion-reduce:animate-none" />
              </span>
            ))
          : null}
        {shown.map((p) => (
          <div key={p.id} role="listitem">
            <AnchoredPopover
              open={open?.id === p.id}
              onOpenChange={(next, details) => {
                // Our own handlers open it; the primitive only reports the ways it closes (Escape, a click outside).
                if (!next && details.reason !== 'trigger-press') setOpen(null);
              }}
            >
              <AnchoredPopoverTrigger
                render={
                  <button
                    type="button"
                    aria-label={`${p.name}, ${p.specialty}, ${
                      p.slotsState === 'error' ? "slots couldn't load" : `${p.slotsToday} slots today`
                    }`}
                    className="cal-chip-button"
                    onMouseEnter={() => show(p.id)}
                    onMouseLeave={hideSoon}
                    onFocus={(e) => {
                      // Keyboard focus only: closing a card hands focus back to the chip, which must not reopen it.
                      if (!e.currentTarget.matches(':focus-visible')) return;
                      cancelClose();
                      clearShow();
                      setOpen((o) => (o?.id === p.id ? o : { id: p.id, pinned: false }));
                    }}
                    onBlur={hideSoon}
                    onClick={() => setOpen((o) => (o?.id === p.id && o.pinned ? null : { id: p.id, pinned: true }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setOpen(null);
                    }}
                  />
                }
              >
                <Chip p={p} ring={narrowed && (selected?.has(p.id) ?? false)} />
              </AnchoredPopoverTrigger>
              <AnchoredPopoverContent
                align="start"
                side="bottom"
                className="cal-chip-card"
                // A hover card never takes focus (the pointer is still on the chip); a pinned one does, so the
                // keyboard reaches its controls. Closing gives focus back to nothing.
                initialFocus={open?.id === p.id && open.pinned}
                finalFocus={false}
                onMouseEnter={cancelClose}
                onMouseLeave={hideSoon}
                onFocus={cancelClose}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setOpen(null);
                }}
              >
                <ProviderBadge
                  name={p.name}
                  specialty={p.specialty}
                  initials={p.initials}
                  photo={p.photo}
                  issue={p.issue}
                  available={p.available}
                  slotsToday={p.slotsToday}
                  schedule={p.schedule}
                  nextSlots={p.nextSlots}
                  nextSlotResponses={p.nextSlotResponses}
                  quickBookGroups={p.quickBookGroups}
                  isSelected={selected ? selected.has(p.id) : false}
                  onToggleSelect={onToggleProvider ? () => onToggleProvider(p.id) : undefined}
                  onClick={onProviderClick ? () => onProviderClick(p.id) : undefined}
                  onSelectProfile={(g, profile) => onSelectProfile?.(p.id, g, profile)}
                  practitionerId={p.id}
                  organizationId={organizationId}
                  renderSlotsPopover={renderSlotsPopover}
                  onSelectSlot={onSelectSlot ? (slot, profile) => onSelectSlot(p.id, slot, profile) : undefined}
                  slotsState={p.slotsState}
                  onRetrySlots={onRetrySlots}
                />
              </AnchoredPopoverContent>
            </AnchoredPopover>
          </div>
        ))}
        {rest > 0 ? (
          <button
            type="button"
            aria-expanded={false}
            aria-label={`Show ${rest} more ${rest === 1 ? 'practitioner' : 'practitioners'}`}
            onClick={() => setExpanded(true)}
            className="cal-chips-more"
          >
            +{rest}
          </button>
        ) : null}
        {expanded ? (
          <button type="button" aria-expanded aria-label="Show fewer practitioners" onClick={() => setExpanded(false)} className="cal-chips-more">
            &minus;
          </button>
        ) : null}
      </div>
    </div>
  );
}
